import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import type { NativeCatalogRoom } from '@/api/core/native-property-read';
import {
  expectedSelectionVersion,
  readRoomSelectionState,
  ROOM_SELECTION_REASON_MAX,
  roomSelectionReasonLength,
  selectRoom,
} from '@/api/core/room-selection';
import { isApiError } from '@/api/http/errors';
import type { OperatorSession } from '@/auth/session';
import { DirtyGuard } from '@/components/dirty-guard';
import { Button, ConfirmDialog, Input } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { UNSAFE_DataRouterContext, type Location } from 'react-router';
import type { PropertyRuntime } from '../property-queries';

const ADMIN_ROLE = 8;

/**
 * 판매 탭은 숨긴 채 입력을 보존하고, 객실 해제·다른 유형 객실 선택·목록 이동으로 섹션이 다시 마운트돼도 초안은 모듈 저장소가
 * 보존한다. 초안이 사라지는 것은 다른 화면으로 나갈 때(확인 후)뿐이므로 확인도 그때만 요구한다.
 */
const leavesSelectionScope = (current: Location, next: Location) =>
  current.pathname !== next.pathname;

/**
 * 저장 전 사유 초안. 같은 화면 안에서 객실을 바꿔도 입력이 사라지지 않게 하되, 세션 generation(계정 전환·재인증)이
 * 바뀌면 다른 행위자에게 넘어가지 않도록 키에 generation을 넣고 옛 generation 초안은 버린다.
 */
const reasonDrafts = new Map<string, string>();
const watchedSessions = new WeakSet<OperatorSession>();
/**
 * 계정 전환·로그아웃·만료는 세션 generation을 올린다. 그 순간 이전 generation의 초안을 메모리에서 바로 지운다
 * (섹션이 마운트돼 있지 않아도). 같은 계정의 토큰 갱신·권한 재확인은 generation을 올리지 않으므로 초안이 유지된다.
 */
const watchSession = (session: OperatorSession, namespace: string) => {
  if (watchedSessions.has(session)) return;
  watchedSessions.add(session);
  session.subscribe(() => {
    pruneDrafts(namespace, session.getSnapshot().generation);
  });
};
/** 테스트에서 보관 중인 초안 수를 확인한다. */
export const roomSelectionDraftCount = () => reasonDrafts.size;
/** 테스트 격리용. 저장되지 않은 선정 사유 초안을 모두 버린다. */
export const clearRoomSelectionDrafts = () => {
  reasonDrafts.clear();
};
const draftKey = (namespace: string, generation: number, roomId: string) =>
  `${namespace}\u0000${String(generation)}\u0000${roomId}`;
const pruneDrafts = (namespace: string, generation: number) => {
  for (const key of reasonDrafts.keys()) {
    const [ns, gen] = key.split('\u0000');
    if (ns === namespace && gen !== String(generation)) reasonDrafts.delete(key);
  }
};

const failureMessage = (error: unknown, expected: number, conflictedAt: number | null) => {
  if (isApiError(error) && error.status === 409)
    return conflictedAt === expected
      ? '최신 상태를 다시 읽어도 선정이 충돌합니다. 입력한 사유를 보존하고 담당자에게 확인을 요청하세요.'
      : '다른 곳에서 선정 상태가 바뀌었습니다. 최신 상태를 다시 읽었고 입력한 사유는 그대로 두었습니다. 확인 후 다시 선정하세요.';
  if (isApiError(error) && error.status === 400)
    return '선정 요청이 거절되었습니다. 사유와 객실 구조를 확인하세요. 입력한 사유는 그대로 두었습니다.';
  if (isApiError(error) && error.status === 403)
    return '객실 관리 대상 선정 권한이 없습니다. ADMIN 역할과 재고 선정 권한이 필요합니다. 입력한 사유는 그대로 두었습니다.';
  return '선정 결과를 확인하지 못했습니다. 현재 상태를 다시 조회해 확인하세요. 같은 사유로 다시 선정해도 중복 처리되지 않습니다. 자동으로 재전송하지 않으며 입력한 사유는 그대로 두었습니다.';
};

/**
 * 객실을 Core 관리 대상으로 선정한다. 선정은 관리권 활성·판매 허용과 별개다.
 * 현재 상태·버전은 GET admin/rooms/{id}/ownerships의 selection_status/selection_version을 따른다.
 */
export function RoomSelectionSection({
  runtime,
  generation,
  room,
  blocked = false,
  onSelected,
}: {
  runtime: PropertyRuntime;
  generation: number;
  room: Pick<NativeCatalogRoom, 'id' | 'name' | 'selected' | 'selection_version'>;
  /** 같은 화면의 다른 명령이 실행 중이거나 결과가 불확실하면 선정을 막는다. */
  blocked?: boolean;
  /** 선정 직후 카탈로그를 다시 읽는다. 완료되면 부모가 새 selection_version을 본다. */
  onSelected: () => Promise<void>;
}) {
  const auth = useSyncExternalStore(runtime.session.subscribe, runtime.session.getSnapshot);
  const scope = draftKey(runtime.apiNamespace, generation, room.id);
  const [reason, setReasonState] = useState(() => {
    pruneDrafts(runtime.apiNamespace, generation);
    return reasonDrafts.get(scope) ?? '';
  });
  const setReason = (value: string) => {
    if (value) {
      watchSession(runtime.session, runtime.apiNamespace);
      reasonDrafts.set(scope, value);
    } else reasonDrafts.delete(scope);
    setReasonState(value);
  };
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const mounted = useRef(true);
  useEffect(() => {
    // 객실·세션 generation이 바뀌면 이전 범위의 진행 표시를 넘기지 않고 그 범위의 초안만 다시 읽는다.
    conflictedAt.current = null;
    setBusy(false);
    setFeedback('');
    setSucceeded(false);
    setConfirming(false);
    setReasonState(reasonDrafts.get(scope) ?? '');
  }, [scope]);
  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const mountedPath = useRef(router?.state.location.pathname);
  useEffect(() => {
    mounted.current = true;
    const namespace = runtime.apiNamespace;
    const startedAt = mountedPath.current;
    return () => {
      // 확인 후 다른 화면(pathname 변경)으로 나가면 초안을 버린다. 안내 문구('나가기: 입력이 사라집니다')와 맞춘다.
      if (router && startedAt !== router.state.location.pathname) pruneDrafts(namespace, -1);
      // 섹션은 객실 해제·다른 유형 객실 선택·목록 이동처럼 이탈 확인이 없는 경로에서도 다시 마운트된다.
      // 그때 초안을 지우지 않는다. 다른 행위자로 넘어가지 않는 경계는 키의 generation과 mount 시 prune이 맡는다.
      mounted.current = false;
    };
  }, [router, runtime.apiNamespace]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [succeeded, setSucceeded] = useState(false);
  const inflight = useRef(false);
  const conflictedAt = useRef<number | null>(null);
  const current = auth.generation === generation && auth.status === 'authorized';
  const authorization = current ? auth.authorization : null;
  const canRead =
    authorization !== null &&
    !auth.deniedDomains.includes('property') &&
    !auth.deniedDomains.includes('inventory') &&
    canReadDomain(authorization, 'property', 1) &&
    canReadDomain(authorization, 'inventory', 16);
  // BE는 INVENTORY bit 1 선정에 ADMIN 역할을 요구한다(STAFF는 거절).
  const canSelect =
    canRead &&
    (authorization.role_bits & ADMIN_ROLE) !== 0 &&
    canReadDomain(authorization, 'inventory', 1);
  const stateQuery = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'room-selection-state', room.id),
      room.selection_version,
    ],
    queryFn: async ({ signal }) => {
      try {
        return await readRoomSelectionState(runtime.client, room.id, signal);
      } catch (error) {
        if (isApiError(error) && error.status === 403)
          runtime.session.reportDomainAccessDenied('inventory', generation);
        throw error;
      }
    },
    enabled: canRead,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const state = canRead && stateQuery.isSuccess ? stateQuery.data : null;
  const fresh = state !== null && !stateQuery.isFetching;
  const reasonLength = roomSelectionReasonLength(reason);
  const reasonValid = reasonLength >= 1 && reasonLength <= ROOM_SELECTION_REASON_MAX;
  const selected = state?.status === 'SELECTED';
  const readyToSelect = fresh && !selected && canSelect && !blocked && !busy;

  const submit = async () => {
    if (inflight.current || !fresh || state.status === 'SELECTED' || !reasonValid) return;
    const latest = runtime.session.getSnapshot();
    if (
      latest.generation !== generation ||
      latest.status !== 'authorized' ||
      !latest.authorization ||
      latest.deniedDomains.includes('inventory') ||
      (latest.authorization.role_bits & ADMIN_ROLE) === 0 ||
      !canReadDomain(latest.authorization, 'inventory', 1)
    ) {
      setFeedback('현재 권한을 다시 확인하세요.');
      return;
    }
    const expected = expectedSelectionVersion(state);
    const started = scope;
    // 응답이 돌아왔을 때 같은 행위자·같은 세션·같은 객실 화면일 때만 화면 상태를 바꾼다.
    // 계정 전환·재인증은 generation을 올리고 권한 상실은 status/authorization을 바꾼다.
    const sameBoundary = () => {
      const now = runtime.session.getSnapshot();
      return (
        mounted.current &&
        scopeRef.current === started &&
        now.generation === generation &&
        now.status === 'authorized'
      );
    };
    inflight.current = true;
    setBusy(true);
    setFeedback('');
    setSucceeded(false);
    let accepted = false;
    try {
      await selectRoom(runtime.client, room.id, { reason, expected_version: expected });
      accepted = true;
      reasonDrafts.delete(started);
      if (sameBoundary()) {
        conflictedAt.current = null;
        setReasonState('');
        setSucceeded(true);
      }
    } catch (error) {
      if (!sameBoundary()) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('inventory', generation);
      if (isApiError(error) && error.status === 409) {
        const previous = conflictedAt.current;
        setFeedback(failureMessage(error, expected, previous));
        conflictedAt.current = expected;
      } else {
        setFeedback(failureMessage(error, expected, null));
      }
    } finally {
      inflight.current = false;
      if (mounted.current && scopeRef.current === started) setBusy(false);
    }
    // 성공이면 카탈로그를 먼저 다시 읽어 selection_version이 바뀐 뒤 소유권을 조회한다.
    // 실패·불확실이면 현재 상태만 다시 조회해 SELECTED가 되었는지 보여 준다.
    if (!sameBoundary()) return;
    if (accepted) {
      try {
        await onSelected();
      } catch {
        if (sameBoundary())
          setFeedback('선정은 완료되었지만 최신 목록을 다시 읽지 못했습니다. 새로고침하세요.');
      }
    }
    if (sameBoundary()) await stateQuery.refetch();
  };

  return (
    <section aria-label="객실 관리 대상 선정" className="space-y-3 rounded-md border p-4">
      {/* 판매 탭의 다른 가드(외부 연동 슬롯·콘텐츠)와 같은 Provider 하나가 blocker를 맡는다. */}
      <DirtyGuard when={reason.trim().length > 0} leaves={leavesSelectionScope} />
      <h4 className="font-medium">{room.name} 관리 대상 선정</h4>
      <p className="body-sm text-muted-foreground">
        선정은 객실 전체를 Core 관리 후보로 기록할 뿐 관리권 활성이나 판매 허용을 뜻하지 않습니다.
        관리권 최초 활성화와 판매 설정은 별도 업무입니다.
      </p>
      {canRead ? null : (
        <p className="body-sm" role="status">
          선정 상태를 보려면 지점·재고 조회 권한이 필요합니다.
        </p>
      )}
      {canRead && stateQuery.isError ? (
        <div className="space-y-2">
          <p role="alert" className="body-sm">
            선정 상태를 불러오지 못했습니다. 다시 조회해 주세요.
          </p>
          <Button
            variant="outline"
            className="h-auto min-h-8 whitespace-normal"
            disabled={stateQuery.isFetching}
            onClick={() => void stateQuery.refetch()}
          >
            선정 상태 다시 조회
          </Button>
        </div>
      ) : null}
      {canRead && !stateQuery.isError && state ? (
        <p role="status" className="body-sm">
          현재 상태:{' '}
          {state.status === 'SELECTED'
            ? `관리 대상 선정됨 · 선정 버전 ${String(state.version)}`
            : state.status === 'NOT_SELECTED'
              ? `관리 대상 아님 · 선정 버전 ${String(state.version)}`
              : '선정 기록 없음'}
          {state.unitOwnerships.length > 0 &&
          state.unitOwnerships.every((unit) => unit.ownershipStatus === null)
            ? ' · 관리권 활성 전'
            : ''}
        </p>
      ) : null}
      {canRead && !stateQuery.isError && !state && (
        <p role="status" className="body-sm">
          선정 상태를 확인하는 중…
        </p>
      )}
      {state && !selected ? (
        canSelect ? (
          <div className="space-y-2">
            <label className="body-sm grid gap-1" htmlFor={`room-selection-reason-${room.id}`}>
              선정 사유
              <Input
                id={`room-selection-reason-${room.id}`}
                value={reason}
                disabled={busy}
                aria-invalid={reason.length > 0 && !reasonValid}
                aria-describedby={`room-selection-help-${room.id}${feedback ? ` room-selection-feedback-${room.id}` : ''}`}
                onChange={(event) => {
                  setReason(event.target.value);
                  setSucceeded(false);
                }}
              />
            </label>
            <p id={`room-selection-help-${room.id}`} className="body-sm text-muted-foreground">
              {String(reasonLength)} / {String(ROOM_SELECTION_REASON_MAX)}자 · 감사 이력에 남습니다.
              저장 전 사유는 이 화면 안에서 객실·탭을 옮겨도 유지되며, 다른 화면으로 나가거나
              로그아웃·계정 전환 시 사라집니다.
            </p>
            <Button
              className="h-auto min-h-8 whitespace-normal"
              disabled={!readyToSelect || !reasonValid}
              onClick={() => {
                setConfirming(true);
              }}
            >
              관리 대상으로 선정
            </Button>
            {!reasonValid && reason.length > 0 && (
              <p role="alert" className="body-sm">
                선정 사유를 1~{String(ROOM_SELECTION_REASON_MAX)}자로 입력하세요.
              </p>
            )}
          </div>
        ) : (
          <p className="body-sm text-muted-foreground">
            객실 관리 대상 선정은 ADMIN 역할과 재고 선정 권한이 필요합니다.
          </p>
        )
      ) : null}
      {busy && (
        <p role="status" className="body-sm">
          선정 요청 처리 중…
        </p>
      )}
      {succeeded && !busy && (
        <p role="status" className="body-sm">
          관리 대상으로 선정했습니다. 관리권은 아직 활성화되지 않았습니다.
        </p>
      )}
      {feedback && (
        <div className="space-y-2">
          <p role="alert" id={`room-selection-feedback-${room.id}`} className="body-sm">
            {feedback}
          </p>
          {canRead && state && !selected && (
            <Button
              variant="outline"
              className="h-auto min-h-8 whitespace-normal"
              disabled={stateQuery.isFetching || busy}
              onClick={() => void stateQuery.refetch()}
            >
              선정 상태 다시 조회
            </Button>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        target={room.name}
        action="객실 관리 대상 선정"
        consequence="객실 전체를 관리 대상으로 기록합니다. 관리권 활성·판매 허용은 변경하지 않습니다."
        variant="default"
        onConfirm={() => {
          setConfirming(false);
          void submit();
        }}
      />
    </section>
  );
}
